import { useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  GripVertical,
  Navigation,
  Pause,
  Play,
  StickyNote,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { LaneChips } from "./atoms";
import type { DerivedStop } from "./trip-stops";

/**
 * The Trip focus stop list.
 *
 * Ported from admin-web's `dispatch-v2/modal-stops.tsx`: a flat row per stop —
 * status badge, street address, lane counts, ETA — with drag-to-reorder behind
 * a mode. Completed stops stay visible and crossed out until the dispatcher
 * explicitly hides them, matching the trip's retain-until-archive lifecycle.
 *
 * Presentational by design. It owns the pointer-following order and nothing
 * else: every mutation belongs to the workflow above it, which already funnels
 * outcomes into one feedback banner. A list that fired its own mutations would
 * report success in a second place and drift from the banner beside it.
 */

function StatusBadge({ stop, color }: { stop: DerivedStop; color: string }) {
  if (stop.allHeld) {
    return (
      <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md bg-zinc-400 text-white">
        <Pause className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }
  if (stop.status === "done") {
    return (
      <span
        className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md text-white"
        style={{ background: "#94a3b8" }}
      >
        <Check className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }
  if (stop.status === "active") {
    return (
      <span
        className="grid h-[22px] w-[22px] shrink-0 animate-pulse place-items-center rounded-md text-white"
        style={{ background: color }}
      >
        <Navigation className="h-[11px] w-[11px]" />
      </span>
    );
  }
  return (
    <span
      className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md font-mono text-[11px] font-bold tabular-nums text-white"
      style={{ background: color }}
    >
      {stop.seq}
    </span>
  );
}

function StopRow({
  stop,
  color,
  selected,
  armed,
  busy,
  first,
  last,
  canStart,
  canRemove,
  onInspect,
  onStart,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  stop: DerivedStop;
  color: string;
  selected: boolean;
  armed: boolean;
  busy: boolean;
  first: boolean;
  last: boolean;
  canStart: boolean;
  canRemove: boolean;
  onInspect: () => void;
  onStart: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stop.id, disabled: !armed || stop.heldCount > 0 });

  const done = stop.status === "done";
  const startable =
    canStart &&
    stop.tasks.some(
      (task) =>
        task.status.toUpperCase() === "PENDING" &&
        !task.cancellationReviewPending &&
        task.workHold?.reason !== "CANCELLATION_REVIEW"
    );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={
        "flex items-center gap-2 rounded-lg py-1.5 pl-1 pr-2 " +
        (isDragging
          ? "bg-indigo-50 ring-1 ring-indigo-200"
          : selected
            ? "bg-indigo-50"
            : stop.allHeld
              ? "bg-zinc-100 text-zinc-500"
              : "hover:bg-zinc-50")
      }
    >
      {armed && stop.heldCount === 0 && (
        <>
          <button
            type="button"
            aria-label={`Reorder stop ${stop.seq}`}
            className="cursor-grab touch-none text-zinc-400 hover:text-zinc-600"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          {/*
            Dragging is the pointer affordance; these are the keyboard one — a
            dispatcher who cannot drag still has to be able to sequence a trip.
            Hidden until focused so the armed row looks like admin's: one grip,
            no button cluster.
          */}
          <span className="sr-only flex items-center focus-within:not-sr-only">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Move stop ${stop.seq} up`}
              disabled={busy || first}
              onClick={onMoveUp}
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Move stop ${stop.seq} down`}
              disabled={busy || last}
              onClick={onMoveDown}
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
          </span>
        </>
      )}

      <button
        type="button"
        aria-label={`Inspect stop ${stop.seq}`}
        aria-pressed={selected}
        onClick={onInspect}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <StatusBadge stop={stop} color={color} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span
              className={
                "truncate text-[12.5px] font-semibold " +
                (done ? "text-zinc-400 line-through" : "text-zinc-900")
              }
            >
              {stop.addr}
            </span>
            {stop.heldCount > 0 && (
              <span className="shrink-0 rounded-full bg-zinc-200 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-zinc-700">
                <Pause className="mr-0.5 inline h-2.5 w-2.5" />
                {stop.heldCount} on hold
              </span>
            )}
            {stop.status === "active" && !stop.allHeld && (
              <span className="shrink-0 rounded-full bg-indigo-600 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-white">
                Next
              </span>
            )}
          </span>
          <span className="block truncate text-[11px] text-zinc-500">
            {/*
              Who is being visited, when the Server knows. Falls back to the
              task count rather than an empty line — a row that loses its
              second line jumps when the data arrives.
            */}
            {stop.contact ??
              `${stop.total} ${stop.total === 1 ? "task" : "tasks"}`}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <LaneChips counts={stop.counts} />
          {stop.note && (
            <span
              title="Carries handling notes"
              className="grid h-[17px] w-[17px] place-items-center rounded-[5px] border border-zinc-900 bg-white text-zinc-900"
            >
              <StickyNote className="h-2.5 w-2.5" />
            </span>
          )}
          {stop.fragile && (
            <span
              title="Fragile"
              className="grid h-[17px] w-[17px] place-items-center rounded-[5px] bg-amber-500 text-white"
            >
              <TriangleAlert className="h-2.5 w-2.5" />
            </span>
          )}
          {stop.window && (
            // Amber, because a booked window is a commitment the ETA is
            // measured against — not just another timestamp.
            <span className="w-[86px] text-right font-mono text-[11px] font-semibold tabular-nums text-amber-600">
              {stop.window}
            </span>
          )}
          {stop.eta && (
            <span className="w-12 text-right font-mono text-[11px] font-semibold tabular-nums text-zinc-500">
              {stop.eta}
            </span>
          )}
        </span>
      </button>

      <div className="flex shrink-0 items-center">
        {startable && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Start stop ${stop.seq}`}
            disabled={busy}
            onClick={onStart}
          >
            <Play className="h-3.5 w-3.5" />
          </Button>
        )}
        {canRemove && stop.heldCount === 0 && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove stop ${stop.seq}`}
            className="text-zinc-400 hover:bg-red-50 hover:text-red-600"
            disabled={busy}
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function ModalStopList({
  stops,
  color,
  selectedId,
  armed,
  busy,
  canStart,
  canRemove,
  onInspect,
  onStart,
  onRemove,
  onReorder,
  renderExpanded,
}: {
  stops: DerivedStop[];
  color: string;
  selectedId: string | null;
  /** Drag-to-reorder is off until a dispatcher arms it. */
  armed: boolean;
  busy: boolean;
  canStart: boolean;
  canRemove: boolean;
  onInspect: (stop: DerivedStop) => void;
  onStart: (stop: DerivedStop) => void;
  onRemove: (stop: DerivedStop) => void;
  onReorder: (stops: DerivedStop[]) => Promise<void>;
  /** Rendered beneath the selected stop — its Tasks, fetched by the caller. */
  renderExpanded?: (stop: DerivedStop) => React.ReactNode;
}) {
  const [order, setOrder] = useState<DerivedStop[]>(stops);
  const [showDone, setShowDone] = useState(true);

  useEffect(() => setOrder(stops), [stops]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const doneCount = order.filter((stop) => stop.status === "done").length;
  const visible =
    showDone || doneCount === 0
      ? order
      : order.filter((stop) => stop.status !== "done");

  /** Apply a new order optimistically, and put it back if the Server says no. */
  const commit = async (next: DerivedStop[]) => {
    setOrder(next);
    try {
      await onReorder(next);
    } catch {
      setOrder(stops);
    }
  };

  const move = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= visible.length) return;
    // Index against `order`, not `visible` — with completed stops hidden the
    // two differ, and sequencing against the filtered view would move the
    // wrong stop.
    const from = order.findIndex((stop) => stop.id === visible[index].id);
    const to = order.findIndex((stop) => stop.id === visible[target].id);
    if (from < 0 || to < 0) return;

    const next = [...order];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return commit(next);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = order.findIndex((stop) => stop.id === active.id);
    const to = order.findIndex((stop) => stop.id === over.id);
    if (from < 0 || to < 0) return;

    const next = [...order];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return commit(next); // the list must not snap back under the pointer
  };

  if (stops.length === 0) {
    return (
      <p className="px-2 py-6 text-center text-[12px] text-zinc-500">
        No stops on this trip yet.
      </p>
    );
  }

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={visible.map((stop) => stop.id)}
          strategy={verticalListSortingStrategy}
        >
          {visible.map((stop, index) => (
            <div key={stop.id}>
              <StopRow
                stop={stop}
                color={color}
                selected={selectedId === stop.id}
                armed={armed}
                busy={busy}
                first={index === 0}
                last={index === visible.length - 1}
                canStart={canStart}
                canRemove={canRemove}
                onInspect={() => onInspect(stop)}
                onStart={() => onStart(stop)}
                onRemove={() => onRemove(stop)}
                onMoveUp={() => move(index, -1)}
                onMoveDown={() => move(index, 1)}
              />
              {selectedId === stop.id && renderExpanded?.(stop)}
            </div>
          ))}
        </SortableContext>
      </DndContext>

      {doneCount > 0 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 h-7 w-full text-[11px] text-zinc-500"
          onClick={() => setShowDone((shown) => !shown)}
        >
          {showDone
            ? `Hide ${doneCount} completed`
            : `Show ${doneCount} completed`}
        </Button>
      )}
    </div>
  );
}

/**
 * Confirmation for removing a stop, shared by the rail and the focus dialog.
 *
 * The wording matters and must not drift between the two: a dispatcher needs
 * to know the Tasks come back rather than disappear, wherever they are
 * standing when they remove the stop.
 */
export function RemoveStopDialog({
  stop,
  pending,
  onOpenChange,
  onConfirm,
}: {
  stop: DerivedStop | null;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={Boolean(stop)} onOpenChange={onOpenChange}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove stop?</AlertDialogTitle>
          <AlertDialogDescription>
            Eligible Tasks will return to the unassigned queue. Completed work
            remains attached for history.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={pending}
            onClick={onConfirm}
          >
            Remove stop
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
